import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { approvalsApi, type PendingPaymentApproval } from '../api/approvals.api';
import { subscribersApi } from '../api/subscribers.api';

function ApprovalRow({
  item,
  onReviewed,
}: {
  item: PendingPaymentApproval;
  onReviewed: () => void;
}) {
  const [acting, setActing] = useState<'approve' | 'reject' | null>(null);

  const reviewMutation = useMutation({
    mutationFn: (approved: boolean) =>
      subscribersApi.reviewPaymentOrder(item.subscriber.id, item.paymentOrder.id, approved),
    onSuccess: (data) => {
      toast.success(data.message);
      onReviewed();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to review payment');
    },
    onSettled: () => setActing(null),
  });

  const handleReview = (approved: boolean) => {
    setActing(approved ? 'approve' : 'reject');
    reviewMutation.mutate(approved);
  };

  const { paymentOrder, subscriber, site } = item;
  const paidAt = paymentOrder.lastStatusCheckedAt ?? paymentOrder.updatedAt;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{subscriber.fullname || 'Unknown'}</div>
        {subscriber.subscriberCode && (
          <div className="text-xs text-gray-500 font-mono">{subscriber.subscriberCode}</div>
        )}
        {subscriber.phone && <div className="text-xs text-gray-500 mt-0.5">{subscriber.phone}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {site.name || (site.crmSiteId != null ? `Site ${site.crmSiteId}` : '−')}
      </td>
      <td className="px-4 py-3 font-mono text-sm text-gray-700">
        {paymentOrder.crmOrderId || '−'}
      </td>
      <td className="px-4 py-3 text-right font-semibold text-gray-900">
        ₹{paymentOrder.totalAmount.toFixed(2)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 text-center">
        {paymentOrder.unpaidInvoiceCount}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
        {paidAt ? new Date(paidAt).toLocaleString() : '−'}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => handleReview(true)}
            disabled={acting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
          >
            {acting === 'approve' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5" />
            )}
            Approve
          </button>
          <button
            type="button"
            onClick={() => handleReview(false)}
            disabled={acting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 text-xs font-medium"
          >
            {acting === 'reject' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <XCircle className="w-3.5 h-3.5" />
            )}
            Reject
          </button>
        </div>
      </td>
    </tr>
  );
}

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['pending-payment-approvals', page, limit],
    queryFn: () => approvalsApi.listPending({ page, limit }),
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pending-payment-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['subscribers'] });
    queryClient.invalidateQueries({ queryKey: ['subscriber-payment-orders'] });
    queryClient.invalidateQueries({ queryKey: ['subscriber-payment-order'] });
  };

  const approvals = data?.approvals ?? [];
  const pagination = data?.pagination;
  const totalPending = pagination?.total ?? 0;

  return (
    <div className="min-w-0 max-w-full">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-amber-600" />
            Approvals
          </h1>
          <p className="text-gray-600 mt-1">
            Review subscriber payments reported as paid by the payment gateway
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>{totalPending}</strong> payment{totalPending !== 1 ? 's' : ''} awaiting your
        confirmation. Approve to clear dues; reject to generate a new payment link.
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            Loading pending approvals…
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-4" />
            <p className="text-red-500">Failed to load approvals</p>
            <button type="button" onClick={() => refetch()} className="mt-4 text-blue-600 hover:text-blue-800 text-sm">
              Try again
            </button>
          </div>
        ) : approvals.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <ShieldCheck className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">No pending approvals</p>
            <p className="text-sm mt-1">Paid orders awaiting review will appear here.</p>
          </div>
        ) : (
          <>
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Subscriber
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Site
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Order ID
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Invoices
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Payment reported
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvals.map((item) => (
                  <ApprovalRow key={item.paymentOrder.id} item={item} onReviewed={invalidate} />
                ))}
              </tbody>
            </table>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
